import { formatName } from '@leland-dev/leland-ui-library';
import { type GetServerSideProps } from 'next';
import React from 'react';

import { type Scalars } from '../../../__generated-gql-types__/globalTypes';
import CoachPage, {
  type CoachPageProps,
} from '../../../components/coach/CoachPage';
import {
  type CoachPageCoachFragment,
  type CoachPageGetCoachBySlugQuery,
  type CoachPageGetCoachBySlugQueryVariables,
  type CoachPageGetUserQuery,
  type CoachPageGetUserQueryVariables,
  type CoachPageUserFragment,
} from '../../../components/coach/__generated-gql-types__/CoachPage.generated';
import {
  CoachPageGetCoachBySlugDocument,
  CoachPageGetUserDocument,
} from '../../../components/coach/__generated-gql-types__/CoachPage.generated';
import Page from '../../../components/page/Page';
import PageContent, {
  getStaticPageContentQuery,
  type PageContentDataProps,
} from '../../../components/page/PageContent';
import { addApolloState, initializeApollo } from '../../../lib/apolloClient';
import { ServerTimeLogger } from '../../../utils/analytics/server-log-time';
import { CATEGORY_REMAP } from '../../../utils/category';
import { getCoachUrl } from '../../../utils/coach';
import { APPLICANT_SITE_URL, CACHE_CONTROL } from '../../../utils/constants';
import { constructServerSideRenderingContext } from '../../../utils/requests';

export interface CoachPageWrapperProps {
  user: Possible<CoachPageUserFragment>;
  coach: Possible<CoachPageCoachFragment>;
  selectedCategorySlug: Nullable<string>;
  selectedCategoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  pageContentProps: PageContentDataProps;
}

const Coach: React.FC<CoachPageWrapperProps> = (props) => {
  const categoryToUse = props.coach?.categoryInformationList?.find(
    ({ category }) => category.slug === props.selectedCategorySlug,
  );

  const formattedCoachName = formatName({
    firstName: props.coach?.user.firstName ?? '',
    lastName: props.coach?.user.lastName ?? '',
  });

  const pageTitleFragments = [formattedCoachName];
  const pageTitle = pageTitleFragments.join(' | ');

  if (categoryToUse) {
    pageTitleFragments.push(`${categoryToUse.category.name} Coach`);
  }

  const canonicalLink = `${APPLICANT_SITE_URL}${getCoachUrl({
    coachSlug: props.coach?.slug,
    categorySlug: null,
  })}`;

  const ogDescription =
    categoryToUse?.qualifications ||
    (props.coach?.bio && props.coach?.bio.length > 0
      ? props.coach.bio
      : (props.coach?.categoryInformationList?.[0]?.qualifications ?? ''));

  return (
    <Page
      title={pageTitle}
      canonicalLink={canonicalLink}
      imageUrl={props.coach?.user.pictureLink ?? undefined}
      description={ogDescription}
    >
      <PageContent initialData={props.pageContentProps}>
        <CoachPage {...props} />
      </PageContent>
    </Page>
  );
};
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type QueryType = {
  coachSlug: string;
  categorySlug?: string;
};

export const getServerSideProps: GetServerSideProps<
  CoachPageProps,
  QueryType
> = async ({ params, query, req, resolvedUrl, res }) => {
  res.setHeader('Cache-Control', CACHE_CONTROL);

  if (!params) {
    return { notFound: true };
  }
  const TimeLoggerClient = new ServerTimeLogger();

  const apolloClient = initializeApollo();
  const categorySlug =
    params.categorySlug ?? (query.category as Possible<string>);
  if (categorySlug && categorySlug in CATEGORY_REMAP) {
    return {
      redirect: {
        permanent: true,
        destination: getCoachUrl({
          coachSlug: params.coachSlug,
          categorySlug: CATEGORY_REMAP[categorySlug],
        }),
      },
    };
  }

  const { data: userData } = await apolloClient.query<
    Possible<CoachPageGetUserQuery>,
    CoachPageGetUserQueryVariables
  >({
    query: CoachPageGetUserDocument,
    context: constructServerSideRenderingContext(req, resolvedUrl),
  });

  const { data } = await apolloClient.query<
    Possible<CoachPageGetCoachBySlugQuery>,
    CoachPageGetCoachBySlugQueryVariables
  >({
    query: CoachPageGetCoachBySlugDocument,
    variables: { ...params, categorySlugOrUrn: categorySlug },
    context: constructServerSideRenderingContext(req, resolvedUrl),
  });

  TimeLoggerClient.logTime('CoachPageGetCoachBySlugQuery');

  const selectedCategory = categorySlug
    ? data?.coachBySlug?.categoryInformationList?.find(
        (category) => category.category.slug === categorySlug,
      )?.category
    : null;

  if (categorySlug && !selectedCategory) {
    return {
      redirect: {
        permanent: false,
        destination: getCoachUrl({
          coachSlug: params.coachSlug,
          categorySlug: null,
        }),
      },
    };
  }

  const pageContentCategoriesHeaderData = await getStaticPageContentQuery();
  TimeLoggerClient.logTime('getStaticPageContentQuery');

  return addApolloState(apolloClient, {
    props: {
      user: userData?.user,
      coach: data?.coachBySlug,
      selectedCategorySlug: selectedCategory?.slug ?? null,
      selectedCategoryUrn: selectedCategory?.id ?? null,
      pageContentProps: pageContentCategoriesHeaderData,
    },
  });
};

export default Coach;
